// model
import AccomodationService from "@/model/Service/AccomodationService"
import ParticipantService from "@/model/Service/ParticipantService"
import GlobalService from "@/model/Service/GlobalService"
import ServiceFactory from "@/model/Service/ServiceFactory"
import ParticipantServiceFamily from "@/model/ServiceFamily/ParticipantServiceFamily"
import AccomodationServiceFamily from "@/model/ServiceFamily/AccomodationServiceFamily"
import GlobalServiceFamily from "@/model/ServiceFamily/GlobalServiceFamily"
import ServiceFamilyFactory from "@/model/ServiceFamily/ServiceFamilyFactory"
import { IServiceFamilyConfig } from "@/model/ServiceFamilyConfig"
import { IAccomodation } from "@/model/Accomodation"
import { ITraveller } from "@/model/Traveller"
import BookingData from "@/classes/BookingData"
import getFamilyTypeForQuoteObject from "@/logic/getFamilyTypeForQuoteObject"
import DisabledServices from "../../logic/errorHandling/DisabledServices"
import token from "@/state/token"
import disabledServices from "@/state/disabledServices"

import { QuoteState, ServiceFamiliesResponseDTO, ServiceQuote } from "@requests"

import * as _ from "lodash"

const getIsParticipantServiceDisabled = (
    fullServiceId: string,
    travellerIndex: number,
): boolean => {
    const tokenStr: string | null = token.value
    if (!tokenStr) throw new Error("tokenStr cannot be undefined")
    const disabledServicesObj: DisabledServices | null = disabledServices.value
    if (!disabledServicesObj) return false
    if (!disabledServicesObj[tokenStr]) return false
    if (!disabledServicesObj[tokenStr]["participant"]) return false
    const disabledServicesForParticipant = disabledServicesObj[tokenStr]["participant"][travellerIndex]
    if (!disabledServicesForParticipant) return false
    return !!disabledServicesForParticipant.includes(fullServiceId)
}
const getAccomodationServiceDisabled = (fullServiceId: string) => {
    const tokenStr: string | null = token.value
    if (!tokenStr) throw new Error("tokenStr cannot be undefined")
    const disabledServicesObj: DisabledServices | null = disabledServices.value
    if (!disabledServicesObj) return false
    if (!disabledServicesObj[tokenStr]) return false
    const disabledServicesAccomodation = disabledServicesObj[tokenStr]["accomodation"]
    if (!disabledServicesAccomodation) return false
    return !!disabledServicesAccomodation.includes(fullServiceId)
}

const processServiceFamily = async (
    bookingData: BookingData,
    familyId: string,
    serviceFamiliesContentResponse: ServiceFamiliesResponseDTO,
    servicesData: { [familyId: string]: ServiceQuote[] }
): Promise<void> => {
    const quoteObjArr: ServiceQuote[] = servicesData[familyId]
    if (!quoteObjArr) return
    // create service family content object in the beginning to get the type (participant vs accomodation vs global)
    const contentObj = serviceFamiliesContentResponse[familyId]

    // get family type
    const anyQuoteObject = quoteObjArr[0]
    const familyType = getFamilyTypeForQuoteObject(anyQuoteObject)

    // create service family
    const serviceFamily = ServiceFamilyFactory.createServiceFamily(familyId, contentObj, familyType);

    // add config
    const serviceFamilyConfig = bookingData.funnelConfigMap.get(familyId)
    if (serviceFamilyConfig) {
        // set config for service family
        serviceFamily.setServiceFamilyConfig(serviceFamilyConfig);
        serviceFamilyConfig.setServiceFamily(serviceFamily)
    }

    // create service objects and interconnect
    quoteObjArr.forEach((quoteObj: ServiceQuote) => {
        // create service
        const priceObj = undefined // DISABLED
        // const priceObj = bookingData.responses.priceResponse?.services?.find((priceObj: ServicePrice) => {
        //     const split = quoteObj.id.split("_")
        //     const id = split[split.length - 1]
        //     return priceObj.serviceId === id
        // })
        if (!quoteObj) throw new Error("quoteObj is mandatory")
        const split = quoteObj.id.split("_")
        const id = split[split.length - 1]
        const responseContentObj = bookingData.responses.serviceContentResponses[id]
        const service = ServiceFactory.createService(priceObj, quoteObj, responseContentObj, serviceFamily)


        // connect accomodations to accomodation type services and families
        if (service instanceof AccomodationService && serviceFamily instanceof AccomodationServiceFamily) {
            bookingData.accomodationsArr.forEach((accomodation: IAccomodation) => {
                service.addAccomodation(accomodation)
                const isServiceSelected: boolean = service.getQuantitySelected() > 0
                const isServiceDisabled: boolean = !isServiceSelected && getAccomodationServiceDisabled(service.getIdFull())

                if (service.getQuantitySelected() > 0) accomodation.addServiceSelected(service)
                if (!isServiceDisabled) {
                    accomodation.addServiceEligible(service) // interconnect all as eligible because at the moment all accomodation options apply to all accomodations, almost like a global option, so...
                }
            })
        }
        // connect travellers to participant type services and families
        else if (service instanceof ParticipantService && serviceFamily instanceof ParticipantServiceFamily) {
            const eligibleTravellersIdsSet: Set<string> = new Set()
            const includedTravellersIdsSet: Set<string> = new Set()
            const selectedTravellersIdsSet: Set<string> = new Set()
            quoteObj.travellers?.forEach((traveller) => {
                eligibleTravellersIdsSet.add(traveller.id)
                if (traveller.included) includedTravellersIdsSet.add(traveller.id)
                if (traveller.selected) selectedTravellersIdsSet.add(traveller.id)
            })

            bookingData.travellersArr.forEach((traveller: ITraveller) => {

                if (includedTravellersIdsSet.has(traveller.getId())) {
                    service.addTravellerIncluded(traveller)
                    traveller.addServiceIncluded(service)
                }
                if (selectedTravellersIdsSet.has(traveller.getId())) {
                    service.addTravellerSelected(traveller)
                    traveller.addServiceSelected(service)
                    traveller.addServiceFamilySelected(serviceFamily)
                    serviceFamily.addTravellerSelected(traveller)
                }
                if (eligibleTravellersIdsSet.has(traveller.getId())) {
                    const isServiceSelected: boolean = service.getIsSelectedForTravellerId(traveller.getId())
                    const isServiceDisabled: boolean = !isServiceSelected && getIsParticipantServiceDisabled(service.getIdFull(), traveller.getIndex())
                    if (!isServiceDisabled) {
                        service.addTravellerEligible(traveller)
                        traveller.addServiceEligible(service)
                        serviceFamily.addTravellerEligible(traveller)
                    }
                }
            })
        }

        // attach global service to lihe
        else if (service instanceof GlobalService && serviceFamily instanceof GlobalServiceFamily) {
            bookingData.lihe.addGlobalService(service)
            if (service.getSelected()) bookingData.lihe.addGlobalServiceSelected(service)
        }


        // interconnect service and service family
        service.setServiceFamily(serviceFamily)
        serviceFamily.addService(service)
        if (service instanceof AccomodationService) {
            if (service.getQuantitySelected() > 0) {
                serviceFamily.addServiceSelected(service)
                bookingData.selectedServicesMap.set(service.getIdFull(), service)
                bookingData.selectedServiceFamiliesMap.set(familyId, serviceFamily)
            }
        } else if (service instanceof ParticipantService) {
            const travellers = bookingData.travellersArr
            travellers.forEach((traveller: ITraveller) => {
                const selectedTravellersIdsSet = new Set(quoteObj.travellers?.map((traveller) => {
                    if (traveller.selected) return traveller.id
                    return null
                }).filter(_ => _)) as Set<string>
                if (selectedTravellersIdsSet.has(traveller.getId())) {
                    serviceFamily.addServiceSelected(service)
                    bookingData.selectedServicesMap.set(service.getIdFull(), service)
                    bookingData.selectedServiceFamiliesMap.set(familyId, serviceFamily)
                }
            })
        }

        // save service
        if (bookingData.servicesMap.get(service.getIdFull())) throw new Error(`service for this id was already created: ${service.getIdFull()}`)
        bookingData.servicesArr.push(service)
        bookingData.servicesMap.set(service.getIdFull(), service)
    })

    // save service family
    if (bookingData.serviceFamiliesMap.get(familyId)) throw new Error(`serviceFamily for this id was already created: ${familyId}`)
    bookingData.serviceFamiliesArr.push(serviceFamily)
    bookingData.serviceFamiliesMap.set(familyId, serviceFamily)
}


const processServices = (bookingData: BookingData, sourceObj: QuoteState) => {
    const serviceFamiliesContentResponse: ServiceFamiliesResponseDTO | null = bookingData.responses.serviceFamiliesContentResponse!
    if (!serviceFamiliesContentResponse) return

    // create service and service family objects, and interconnect with eachother and accomodations and travellers if needed
    const servicesData: { [familyId: string]: ServiceQuote[] } = _.cloneDeep(sourceObj.services)

    // console.log("servicesData: ", servicesData)
    const processedFamilyIds: Set<string> = new Set()

    bookingData.funnelConfigArr.forEach((configItem: IServiceFamilyConfig) => {
        const serviceFamilyId = configItem.getFamilyId()
        processServiceFamily(bookingData, serviceFamilyId, serviceFamiliesContentResponse, servicesData)
        processedFamilyIds.add(serviceFamilyId)
    })

    const serviceFamiliesResponseObj = bookingData.responses?.bookingQuoteResponse?.state?.services
    if (!serviceFamiliesResponseObj) throw new Error("this cannot be undefined here")
    Object.keys(serviceFamiliesResponseObj).forEach((serviceFamilyId: string) => {
        if (processedFamilyIds.has(serviceFamilyId)) return
        processServiceFamily(bookingData, serviceFamilyId, serviceFamiliesContentResponse, servicesData)
        processedFamilyIds.add(serviceFamilyId)
    })
}

export default processServices